import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CircleAlert, RefreshCw } from "lucide-react";
import { PageHeading, PageShell } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CartPanel } from "@/components/cart/CartPanel";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { useStorefront } from "@/lib/storefront";
import { storefrontSdk } from "@/lib/sdk";
import {
  canMergeUnfinishedCart,
  readUnfinishedCheckoutCart,
  removeUnfinishedCheckoutCart,
  unfinishedCheckoutCartKey,
  type UnfinishedCheckoutCart,
} from "@/lib/unfinished-checkout-cart";

export const Route = createFileRoute("/cart")({
  component: CartPage,
});

function CartPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { cart, lineCount, clear, restoreUnfinished } = useCart();
  const { isAuthenticated, user, openLogin } = useAuth();
  const storefront = useStorefront();
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [savedEntry, setSavedEntry] = useState<{
    key: string;
    snapshot: UnfinishedCheckoutCart | null;
  } | null>(null);
  const recoveryKey =
    isAuthenticated && user?.id
      ? unfinishedCheckoutCartKey(storefront.tenant.id, user.id)
      : null;
  const saved = savedEntry?.key === recoveryKey ? savedEntry.snapshot : null;
  const attempt = useQuery({
    queryKey: ["payment-attempt", saved?.attemptId],
    queryFn: () => storefrontSdk.paymentAttempt(saved!.attemptId),
    enabled: Boolean(saved && isAuthenticated),
    retry: false,
  });

  useEffect(() => {
    setSavedEntry(
      recoveryKey
        ? {
            key: recoveryKey,
            snapshot: readUnfinishedCheckoutCart(window.localStorage, recoveryKey),
          }
        : null,
    );
  }, [recoveryKey]);

  useEffect(() => {
    if (!saved || !recoveryKey || attempt.data?.status !== "succeeded") return;
    removeUnfinishedCheckoutCart(window.localStorage, recoveryKey, saved.attemptId);
    setSavedEntry(null);
  }, [attempt.data?.status, recoveryKey, saved]);

  async function restoreSavedItems(replaceCurrent: boolean) {
    if (!saved || !recoveryKey || restoring) return;
    setRestoring(true);
    try {
      const latest = await attempt.refetch();
      if (!latest.data || latest.data.status === "succeeded") return;
      restoreUnfinished(saved.cart, replaceCurrent);
      removeUnfinishedCheckoutCart(window.localStorage, recoveryKey, saved.attemptId);
      setSavedEntry(null);
      setConfirmReplace(false);
    } finally {
      setRestoring(false);
    }
  }

  const earlierPaymentPending =
    attempt.data &&
    !["failed", "expired", "cancelled"].includes(attempt.data.status);

  return (
    <PageShell className="max-w-6xl">
      <PageHeading
        breadcrumbs={[{ label: t("nav.home"), to: "/" }, { label: t("nav.menu"), to: "/menu" }, { label: t("cart.title") }]}
        back
        backTo="/menu"
        title={t("cart.title")}
        description={t(lineCount === 1 ? "cart.line" : "cart.lines", { count: lineCount })}
        action={
          <Button asChild variant="outline">
            <Link to="/menu">
              <ArrowRight className="size-4 ltr:rotate-180" aria-hidden="true" />
              {t("cart.continueShopping")}
            </Link>
          </Button>
        }
      />

      {saved && attempt.data?.status !== "succeeded" ? (
        <section className="surface-card mb-5 flex flex-col gap-4 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <CircleAlert className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden="true" />
            <div>
              <h2 className="font-bold">{t("cart.unfinishedTitle")}</h2>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">
                {attempt.isError
                  ? t("cart.unfinishedError")
                  : attempt.isPending
                    ? t("cart.unfinishedChecking")
                    : t(
                        earlierPaymentPending
                          ? "cart.unfinishedPending"
                          : "cart.unfinishedFailed",
                      )}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={!attempt.data || attempt.isFetching || restoring}
              onClick={() => {
                if (canMergeUnfinishedCart(cart, saved.cart)) {
                  void restoreSavedItems(false);
                } else {
                  setConfirmReplace(true);
                }
              }}
            >
              {t("cart.unfinishedRestore")}
            </Button>
            <Button asChild variant="outline">
              <Link to="/orders/$orderId" params={{ orderId: saved.orderId }}>
                {t("cart.unfinishedViewOrder")}
              </Link>
            </Button>
            {attempt.isError ? (
              <Button variant="ghost" onClick={() => void attempt.refetch()}>
                <RefreshCw className="size-4" aria-hidden="true" />
                {t("common.retry")}
              </Button>
            ) : null}
          </div>
        </section>
      ) : null}

      <CartPanel
        variant="page"
        onClear={lineCount > 0 ? () => setConfirmClear(true) : undefined}
        onCheckout={() => {
          if (isAuthenticated && user?.verified) return true;
          openLogin(() => navigate({ to: "/checkout" }));
          return false;
        }}
      />

      <ConfirmDialog
        open={confirmClear}
        onOpenChange={setConfirmClear}
        title={t("cart.clearTitle")}
        description={t("cart.clearDesc")}
        confirmLabel={t("cart.clearAction")}
        onConfirm={() => {
          clear();
          setConfirmClear(false);
        }}
      />
      <ConfirmDialog
        open={confirmReplace}
        onOpenChange={setConfirmReplace}
        title={t("cart.unfinishedReplaceTitle")}
        description={t("cart.unfinishedReplaceDescription")}
        confirmLabel={t("cart.unfinishedReplaceAction")}
        onConfirm={() => void restoreSavedItems(true)}
      />
    </PageShell>
  );
}
